import { type FC, useMemo } from 'react';
import { type TreatmentGroup } from '@/model/TreatmentGroup.model.ts';
import { ModalActions, ModalContainer, ModalHeader, useModalAction } from '@/utils/modal.tsx';
import type { TaskTarget } from '@/model/Task.model.ts';
import { getTargetNameForTasks } from '@/components/Tasks/Show/Tasks.utils.ts';
import { sortAscByProperty } from '@/utils/array.utils';
import { web } from '@/support/route.ts';
import Link from '@/components/UI/Link';
import { _notNil } from '@/littledash.ts';
import { TaskTargetContent } from './TaskTargetContent';

interface TaskTargetDetailsProps {
  target: TaskTarget;
  groups: Array<TreatmentGroup>;
  studyId: string | null;
  taskTitle: string;
  subText?: string;
  emptyMessage?: string;
}

const getGroupKey = (group: TreatmentGroup): string => {
  return (group.api_id ?? group.temp_id) as string;
};

export const TaskTargetDetails: FC<TaskTargetDetailsProps> = ({
  target,
  groups,
  studyId,
  taskTitle,
  subText,
  emptyMessage,
}) => {
  const { closeModal } = useModalAction();

  const groupsById = useMemo(() => {
    return groups.reduce<Map<string, TreatmentGroup>>((acc, group) => {
      const key = getGroupKey(group);
      if (_notNil(key)) {
        acc.set(key, group);
      }
      return acc;
    }, new Map());
  }, [groups]);

  const targetGroups = useMemo(() => {
    return target.type === 'group'
      ? target.groups
          .reduce<Array<TreatmentGroup>>((acc, id) => {
            const group = groupsById.get(id);
            if (_notNil(group)) {
              acc.push(group);
            }
            return acc;
          }, [])
          .sort(sortAscByProperty('no'))
      : [];
  }, [groupsById, target]);

  return (
    <ModalContainer size="small" data-testid="task-target-details-modal">
      <ModalHeader
        title={taskTitle}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subText={subText ?? `This task applies to ${getTargetNameForTasks(target).toLowerCase()}`}
      />
      <div className="pa3 overflow-y-auto" style={{ maxHeight: '500px' }} data-testid="task-target-content">
        <TaskTargetContent target={target} targetGroups={targetGroups} emptyMessage={emptyMessage} />
      </div>
      {_notNil(studyId) && (
        <ModalActions
          className="pa3 bt b--moon-gray"
          asideComponent={
            <Link className="link" to={web('studies.studyGroups', { id: studyId })} onClick={() => closeModal()}>
              Go to treatment groups
            </Link>
          }
        />
      )}
    </ModalContainer>
  );
};
